// Sprint 6 (Phase 2, D48) — AggregationCard.
// Sprint ai-elements-rendering (Phase 2, T+X) — restyled to the AI Elements
// ChainOfThoughtStep language (ADR D-026): icon rail + connector + muted
// completed tone, matching ToolCallRow. Behaviour unchanged: aggregate.ts
// grouping untouched, D-msg-8 local override preserved, all testids kept.
//
// Visual sibling of `FounderToolRow`: a single collapsed row with an icon,
// humanized count headline, and a status glyph that aggregates the children.
// Expanding `▽` reveals the original list of `FounderToolRow`s — each one
// retains its own `▽` so the founder can drill into a specific tool call's
// developer detail without flipping the global toggle.

import { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from '../atoms/Icon';
import { MIXED_TOOL_NAME, type AggregationItem } from '../../lib/tool-rendering/aggregate';
import {
  formatActionDuration,
  formatClockTime,
} from '../../lib/tool-rendering/format-elapsed';
import { fallbackHeadline } from '../../lib/tool-rendering/verb-map';
import { useSettingsStore } from '../../stores/settings.store';
import { useTranslation } from '../../i18n';
import { FounderToolRow } from './FounderToolRow';
import { CollapseHeaderRow } from './CollapseHeaderRow';
import type { Message } from '../../types';
import type { RowLivenessCtx } from '../../lib/tool-rendering/founder-layer';

export interface AggregationCardProps {
  /** Sprint orphan-tool-rows-honest (D013-EC-147) — per-message
   * plausibly-live ctx, forwarded to each expanded child row so an
   * orphaned child renders interrupted, not running. */
  ctxForMessage?: (m: Message) => RowLivenessCtx;
  item: AggregationItem;
  /** Map from tool_use_id → tool_result Message, used by child rows. */
  resultByToolUseId: Map<string, import('../../types').Message>;
}

export function AggregationCard({ item, resultByToolUseId, ctxForMessage }: AggregationCardProps) {
  const { t } = useTranslation();
  const developerMode = useSettingsStore((s) => s.developerMode);
  // Sprint messages-section-fixes (D-msg-8) — same per-row override
  // preservation as ToolCallRow. The local toggle survives global
  // developer-mode flips once the founder has explicitly touched it.
  const [open, setOpen] = useState<boolean>(developerMode);
  const userTouchedRef = useRef<boolean>(false);
  useEffect(() => {
    if (userTouchedRef.current) return;
    setOpen(developerMode);
  }, [developerMode]);
  const handleToggle = () => {
    userTouchedRef.current = true;
    setOpen((v) => !v);
  };

  // A mixed run has no tool-specific verb or icon that would be true of
  // all its members (D2), so it takes the generic one.
  const isMixed = item.toolName === MIXED_TOOL_NAME;
  // D2 — the mixed-run copy is resolved here, where a translator exists.
  const headline = isMixed ? t('actionRow.countActions', { count: item.count }) : item.headline;
  const fallback = fallbackHeadline(item.toolName, {});
  const resolvedIconName = (isMixed ? 'Cog' : fallback.iconName ?? 'Cog') as IconName;

  // D5/D6 — clock time and total duration on the row itself.
  const clock = formatClockTime(item.lastActivityAtMs);
  const duration = formatActionDuration(item.durationMs, t);
  // D4 — a compacted group NEVER hides a failure behind an icon. The row
  // says it in words, in the error tone, so "no leo las acciones" (Santi
  // 2026-08-13) stays safe: he opens the ones that say something broke.
  const failureLabel =
    item.errorCount > 0
      ? item.errorCount === 1
        ? t('actionRow.failedOne')
        : t('actionRow.failedMany', { count: item.errorCount })
      : null;

  const isError = item.errorCount > 0;
  const isRunning = item.runningCount > 0 && item.errorCount === 0;
  // Step-language tones (chain-of-thought.tsx statusStyles) — same
  // mapping as ToolCallRow: running=foreground, done=muted, error=status.
  const headlineColor = isError
    ? 'text-status-error'
    : isRunning
      ? 'text-foreground'
      : 'text-muted-foreground';
  const iconColor = isError ? 'text-status-error' : 'text-muted-foreground';

  // D4 — the failure is a SIBLING of the headline, not its tail: inside
  // the truncating span it was the first thing an ellipsis ate while the
  // clock and the icon survived, which inverts the rule it exists to
  // serve ("a group never hides a failure"). Gate 2 cycle 1.
  // The meta join is computed here (not inside CollapseHeaderRow) so an
  // empty join stays falsy and the span renders exactly as before the
  // extraction (B6 byte-identity trap).
  const meta = [clock, duration].filter(Boolean).join(' · ') || null;

  return (
    <CollapseHeaderRow
      icon={resolvedIconName}
      iconClassName={iconColor}
      headline={headline}
      headlineClassName={headlineColor}
      failureLabel={failureLabel}
      failureTestId="aggregation-failures"
      meta={meta}
      metaTestId="aggregation-meta"
      trailing={
        <AggregationStatusGlyph isError={isError} isRunning={isRunning} errorCount={item.errorCount} t={t} />
      }
      open={open}
      onToggle={handleToggle}
      rootTestId="aggregation-card"
      headerTestId="aggregation-header"
      rootProps={{ 'data-tool': item.toolName, 'data-count': item.count, 'data-error-count': item.errorCount }}
    >
      {open && (
        <div className="space-y-1 pb-1" data-testid="aggregation-children">
          {item.items.map((m) => {
            const c = m.content as { tool_use_id?: unknown };
            const toolUseId = typeof c?.tool_use_id === 'string' ? c.tool_use_id : '';
            const result = resultByToolUseId.get(toolUseId);
            return (
              <FounderToolRow
                key={m.id}
                toolUseMessage={m}
                toolResultMessage={result}
                livenessCtx={ctxForMessage?.(m)}
              />
            );
          })}
        </div>
      )}
    </CollapseHeaderRow>
  );
}

function AggregationStatusGlyph({
  isError,
  isRunning,
  errorCount,
  t,
}: {
  isError: boolean;
  isRunning: boolean;
  errorCount: number;
  t: (key: import('../../i18n').TKey, params?: Record<string, string | number>) => string;
}) {
  if (isRunning) {
    return (
      <Icon
        name="Loader2"
        className="shrink-0 text-muted-foreground animate-spin"
        size={14}
        aria-label={t('aggregation.running')}
      />
    );
  }
  if (isError) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-status-error"
        aria-label={t('aggregation.errored', { count: errorCount })}
      >
        <Icon name="X" size={14} />
        {errorCount > 1 && (
          <span className="text-2xs font-medium" /* design-discipline: kept as text-2xs — tiny error badge */>{errorCount}</span>
        )}
      </span>
    );
  }
  return (
    <Icon
      name="Check"
      className="shrink-0 text-status-success"
      size={14}
      aria-label={t('aggregation.completed')}
    />
  );
}
